import { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, Loader2, Search, StickyNote } from 'lucide-react';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import { useQuickLog } from '../components/QuickLogProvider';
import type { Catalogs, Contact } from '../types/api';
import ContactEditModal, {
  fromContact,
  type ContactFormState,
} from '../components/ContactEditModal';
import NotesModal, { type NotesTarget } from '../components/NotesModal';
import Pager from '../components/Pager';
import FilterChip from '../components/FilterChip';
import SortableTh, { type SortState } from '../components/SortableTh';

// V0 Discovery Calls sheet — slice 06. One row per contact who ever
// transitioned to discovery_call status (sticky via status_events).
// Inline edits target the contact's discovery_call calls row, lazily
// created on first PATCH.

interface DiscoveryRow {
  contact_id: number;
  name: string;
  platform_id: number | null;
  platform: string | null;
  platform_label: string | null;
  current_status_id: number | null;
  status: string | null;
  status_label: string | null;
  current_warmth_tier_id: number | null;
  warmth: string | null;
  warmth_label: string | null;
  current_contact_method_id: number | null;
  contact_method: string | null;
  contact_method_label: string | null;
  latest_sent_at: string | null;
  call_id: number | null;
  booked_at: string | null;
  scheduled_at: string | null;
  link_sent: string | null;
  call_outcome_id: number | null;
  outcome: string | null;
  outcome_label: string | null;
  note_count: number;
}

interface ListResponse {
  rows: DiscoveryRow[];
  total: number;
  page: number;
  size: number;
}

const PAGE_SIZE = 50;

// Statuses this grid may SET, in catalog sort_order. Everything else is either
// a stage the contact is already past (the grid is sticky on having a
// discovery_call event) or does not apply here. Ordering is still the
// catalog's — this only filters. See the Status <select> for the full rationale.
const DISCOVERY_STATUSES: string[] = [
  'discovery_call',
  'client_hold',
  'client_won',
  'not_now',
  'not_interested',
  'ghosted',
];

// Must match LegacyChats.tsx — the same status renders on both grids.
const STATUS_TONE: Record<string, string> = {
  sent: 'bg-brand-mint text-brand-forest',
  replied: 'bg-brand-sky text-brand-forest',
  interested: 'bg-brand-lime text-brand-forest',
  booked: 'bg-brand-lime-700 text-white',
  discovery_call: 'bg-brand-violet text-white',
  client_hold: 'bg-brand-sun text-brand-forest',
  client_won: 'bg-brand-lime-700 text-white',
  follow_up: 'bg-brand-coral text-white',
  not_now: 'bg-brand-sage text-white',
  ghosted: 'bg-brand-forest text-white',
};

// Retains tones for outcomes migration 0014 SOFT-deleted (client_won,
// client_no, client_hold, strong_fit, warming_up_maybe, not_a_fit). They are
// gone from the dropdown — /catalogs filters deleted_at IS NULL — but the
// grid's LEFT JOIN has no such filter, so historical rows still render their
// label and still need a tone. Do not prune these as unused.
const OUTCOME_TONE: Record<string, string> = {
  call_completed: 'bg-brand-lime-700 text-white',
  call_couldnt_attend: 'bg-brand-coral text-white',
  missed_call: 'bg-brand-sage text-white',
  client_won: 'bg-brand-lime-700 text-white',
  client_no: 'bg-brand-sage text-white',
  client_hold: 'bg-brand-sun text-brand-forest',
  strong_fit: 'bg-brand-lime text-brand-forest',
  warming_up_maybe: 'bg-brand-mint text-brand-forest',
  not_a_fit: 'bg-brand-coral text-white',
  reschedule: 'bg-brand-sky text-brand-forest',
};

type PatchBody = Partial<{
  booked_at: string | null;
  scheduled_at: string | null;
  link_sent: string | null;
  call_outcome_id: number | null;
  status_id: number | null;
}>;

export default function DiscoveryCalls() {
  const apiFetch = useApi();
  const { catalogs } = useCatalogs();
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editingContact, setEditingContact] = useState<ContactFormState | null>(null);
  const [notesTarget, setNotesTarget] = useState<NotesTarget | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search box so type-ahead hits the server ~250ms after the
  // last keystroke rather than on every character.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const openContact = useCallback(
    async (contactId: number) => {
      try {
        const r = await apiFetch(`/contacts/${contactId}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d: { contact: Contact } = await r.json();
        setEditingContact(fromContact(d.contact));
      } catch (e) {
        setError(String(e));
      }
    },
    [apiFetch],
  );

  const openNotes = useCallback(
    (contactId: number, name: string) =>
      setNotesTarget({ contactId, contactName: name, callType: 'discovery_call' }),
    [],
  );

  // Each chip is an independent toggle; "All" clears the set. Filter and sort
  // changes both reset to page 1 — the current page number is meaningless
  // once the result set is reordered or resized.
  const toggleStatus = useCallback((shortName: string) => {
    setStatusFilter((cur) =>
      cur.includes(shortName)
        ? cur.filter((s) => s !== shortName)
        : [...cur, shortName],
    );
    setPage(1);
  }, []);

  const changeSort = useCallback((next: SortState) => {
    setSort(next);
    setPage(1);
  }, []);

  const refetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      if (statusFilter.length) params.set('status', statusFilter.join(','));
      params.set('sort', sort.key);
      params.set('dir', sort.dir);
      params.set('page', String(page));
      params.set('size', String(PAGE_SIZE));
      const r = await apiFetch(`/discovery-calls?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: ListResponse = await r.json();
      setData(d);
    } catch (e) {
      setError(String(e));
    }
  }, [apiFetch, debouncedSearch, statusFilter, sort, page]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // A quick-log can move a contact to discovery_call → new row appears.
  const { onLogged } = useQuickLog();
  useEffect(() => {
    return onLogged(() => {
      refetch();
    });
  }, [onLogged, refetch]);

  async function patchRow(contactId: number, patch: PatchBody) {
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        rows: d.rows.map((row) =>
          row.contact_id === contactId ? applyOptimistic(row, patch, catalogs) : row,
        ),
      };
    });
    try {
      const r = await apiFetch(`/discovery-calls/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: { row: DiscoveryRow } = await r.json();
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          rows: cur.rows.map((row) =>
            row.contact_id === contactId ? d.row : row,
          ),
        };
      });
    } catch (e) {
      setError(String(e));
      refetch();
    }
  }

  async function removeFromList(contactId: number, name: string) {
    if (
      !confirm(
        `Remove "${name}" from the Discovery Calls list? Their booked/completed ` +
          `discovery-call data will be cleared.`,
      )
    )
      return;
    try {
      const r = await apiFetch(`/discovery-calls/${contactId}`, {
        method: 'DELETE',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      refetch();
    } catch (e) {
      setError(String(e));
    }
  }

  const rows = data?.rows ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex items-center gap-3">
        <Phone className="h-6 w-6 text-brand-violet" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Discovery Calls
        </h1>
        {data && (
          <span className="text-sm text-brand-sage">
            {data.total} {data.total === 1 ? 'contact' : 'contacts'}
          </span>
        )}
      </div>

      <p className="text-sm text-brand-sage">
        Every contact who has ever reached <strong>Discovery Call</strong> status
        appears here. Once on the grid, they stay — even if status changes later.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-sage" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name…"
            className="w-full rounded-md border border-brand-sage/40 bg-white py-2 pl-9 pr-3 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
          />
        </div>
        {data && (
          <Pager
            page={data.page}
            size={data.size}
            total={data.total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        )}
      </div>

      {/* Chips are scoped to DISCOVERY_STATUSES — the statuses this grid deals
          in. A row whose current status was set elsewhere to something outside
          that list (e.g. follow_up from the Legacy Chats grid) still appears
          under "All"; it just has no chip of its own. */}
      <div className="flex flex-wrap gap-2">
        <FilterChip
          label="All"
          active={statusFilter.length === 0}
          onClick={() => {
            setStatusFilter([]);
            setPage(1);
          }}
        />
        {catalogs?.outreach_statuses
          .filter((s) => DISCOVERY_STATUSES.includes(s.short_name))
          .map((s) => (
            <FilterChip
              key={s.id}
              label={s.description}
              active={statusFilter.includes(s.short_name)}
              tone={STATUS_TONE[s.short_name]}
              onClick={() => toggleStatus(s.short_name)}
            />
          ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {data == null ? (
        <div className="flex items-center gap-2 text-brand-sage">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-sage/40 bg-white p-10 text-center">
          <Phone className="mx-auto mb-3 h-8 w-8 text-brand-sage" />
          <p className="text-brand-forest">
            {debouncedSearch || statusFilter.length
              ? 'No contacts match the current filter.'
              : 'No discovery calls yet. Move a contact to "Discovery Call" status from the Legacy Spark Chats grid to see them here.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-brand-sage/30 bg-white shadow-sm">
            <table className="min-w-full">
              <thead className="sticky top-0 z-10 bg-brand-paper">
                <tr className="border-b border-brand-sage/20 text-left text-xs font-bold uppercase tracking-wider text-brand-sage">
                  <SortableTh
                    label="Name"
                    sortKey="name"
                    sort={sort}
                    onSort={changeSort}
                    className="sticky left-0 z-20 border-r border-brand-sage/20 bg-brand-paper"
                  />
                  <SortableTh label="Platform" sortKey="platform" sort={sort} onSort={changeSort} />
                  <SortableTh label="Link sent" sortKey="link_sent" sort={sort} onSort={changeSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={changeSort} />
                  <SortableTh label="Booked" sortKey="booked" sort={sort} onSort={changeSort} />
                  <SortableTh label="Appt." sortKey="appt" sort={sort} onSort={changeSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={sort} onSort={changeSort} />
                  <th className="px-3 py-3">Notes</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row
                    key={row.contact_id}
                    row={row}
                    catalogs={catalogs}
                    onPatch={patchRow}
                    onOpenContact={openContact}
                    onOpenNotes={openNotes}
                    onRemoveFromList={removeFromList}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ContactEditModal
        form={editingContact}
        onClose={() => setEditingContact(null)}
        onSaved={() => {
          setEditingContact(null);
          refetch();
        }}
        onChanged={refetch}
      />

      <NotesModal
        target={notesTarget}
        onClose={() => setNotesTarget(null)}
        onChanged={refetch}
      />
    </div>
  );
}

interface RowProps {
  row: DiscoveryRow;
  catalogs: Catalogs | null;
  onPatch: (contactId: number, patch: PatchBody) => Promise<void>;
  onOpenContact: (contactId: number) => void;
  onOpenNotes: (contactId: number, name: string) => void;
  onRemoveFromList: (contactId: number, name: string) => void;
}

function Row({
  row,
  catalogs,
  onPatch,
  onOpenContact,
  onOpenNotes,
  onRemoveFromList,
}: RowProps) {
  // scheduled_at is a TIMESTAMP — show / edit just the date portion for V0
  // parity (the "Appt. Date" cell in V0 only stored a date).
  const scheduledDate = row.scheduled_at ? row.scheduled_at.slice(0, 10) : '';

  // Filter the outcome dropdown to discovery_call-scoped outcomes (plus any
  // generic / null-typed ones reserved for future use) — mirrors the Legacy
  // Chats grid so each grid only offers its own outcomes.
  const discoveryTypeId = useMemo(
    () => catalogs?.call_types.find((t) => t.short_name === 'discovery_call')?.id ?? null,
    [catalogs],
  );
  const outcomeOptions = useMemo(
    () =>
      (catalogs?.call_outcomes ?? []).filter(
        (o) => o.call_type_id === null || o.call_type_id === discoveryTypeId,
      ),
    [catalogs, discoveryTypeId],
  );

  return (
    <tr className="border-b border-brand-sage/10 align-top last:border-0 hover:bg-brand-mint-100/30">
      <td className="sticky left-0 z-10 border-r border-brand-sage/20 bg-white px-3 py-3">
        <button
          type="button"
          onClick={() => onOpenContact(row.contact_id)}
          className="text-left font-bold text-brand-forest underline decoration-transparent underline-offset-2 transition hover:text-brand-lime-700 hover:decoration-brand-lime"
        >
          {row.name}
        </button>
      </td>

      <td className="px-3 py-3 text-sm text-brand-forest">
        {row.platform_label ?? '—'}
      </td>

      <td className="px-3 py-3">
        <input
          type="date"
          value={row.link_sent ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              link_sent: e.target.value === '' ? null : e.target.value,
            })
          }
          className="rounded-md border border-brand-sage/40 bg-white px-2 py-1 text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
        />
      </td>

      <td className="px-3 py-3">
        <select
          value={row.current_status_id ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              status_id: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className={[
            'rounded-md border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-lime/30',
            row.status && STATUS_TONE[row.status]
              ? `border-transparent ${STATUS_TONE[row.status]}`
              : 'border-brand-sage/40 bg-white text-brand-forest',
          ].join(' ')}
        >
          <option value="">—</option>
          {/* Allowlist, not a denylist: this grid is sticky on having a
              discovery_call event, so every pre-discovery stage (new, sent,
              replied, interested, booked) is somewhere the contact already is
              past and must not be re-selectable here. 'booked' specifically
              means "Legacy Spark Chat booked" — outreach_quick_add.py stamps
              legacy_chat.booked_at for it — so picking it would log a spark-chat
              booking for a discovery call and inflate Legacy Chats Scheduled
              (migration 0013 repairs rows produced before it was removed).
              'follow_up' is excluded because the follow-up pattern is not
              applied to discovery calls.

              The `|| s.id === row.current_status_id` escape hatch keeps a row's
              existing value visible even when it is no longer offered, so it
              renders its real status instead of going blank. */}
          {catalogs?.outreach_statuses
            .filter(
              (s) =>
                DISCOVERY_STATUSES.includes(s.short_name) ||
                s.id === row.current_status_id,
            )
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.description}
              </option>
            ))}
        </select>
      </td>

      <td className="px-3 py-3">
        <input
          type="date"
          value={row.booked_at ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              booked_at: e.target.value === '' ? null : e.target.value,
            })
          }
          className="rounded-md border border-brand-sage/40 bg-white px-2 py-1 text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
        />
      </td>

      <td className="px-3 py-3">
        <input
          type="date"
          value={scheduledDate}
          onChange={(e) =>
            onPatch(row.contact_id, {
              scheduled_at: e.target.value === '' ? null : e.target.value,
            })
          }
          className="rounded-md border border-brand-sage/40 bg-white px-2 py-1 text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
        />
      </td>

      <td className="px-3 py-3">
        <select
          value={row.call_outcome_id ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              call_outcome_id:
                e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className={[
            'rounded-md border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/30',
            row.outcome && OUTCOME_TONE[row.outcome]
              ? `border-transparent font-bold ${OUTCOME_TONE[row.outcome]}`
              : 'border-brand-sage/40 bg-white text-brand-forest',
          ].join(' ')}
        >
          <option value="">—</option>
          {outcomeOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.description}
            </option>
          ))}
        </select>
      </td>

      <td className="px-3 py-3">
        <button
          type="button"
          onClick={() => onOpenNotes(row.contact_id, row.name)}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-brand-sage/40 bg-white px-3 py-1.5 text-sm font-bold text-brand-forest transition hover:border-brand-lime hover:text-brand-lime-700"
        >
          <StickyNote className="h-4 w-4" />
          Notes{row.note_count > 0 ? ` (${row.note_count})` : ''}
        </button>
      </td>

      <td className="px-3 py-3">
        <button
          type="button"
          onClick={() => onRemoveFromList(row.contact_id, row.name)}
          className="whitespace-nowrap rounded-md border border-red-200 px-2 py-1 text-xs font-bold text-red-600 transition hover:bg-red-50"
        >
          Remove from list
        </button>
      </td>
    </tr>
  );
}

function applyOptimistic(
  row: DiscoveryRow,
  patch: PatchBody,
  catalogs: Catalogs | null,
): DiscoveryRow {
  let next: DiscoveryRow = { ...row };
  if ('status_id' in patch) {
    const id = patch.status_id ?? null;
    const cat = catalogs?.outreach_statuses.find((s) => s.id === id) ?? null;
    next = {
      ...next,
      current_status_id: id,
      status: cat?.short_name ?? null,
      status_label: cat?.description ?? null,
    };
  }
  if ('call_outcome_id' in patch) {
    const id = patch.call_outcome_id ?? null;
    const cat = catalogs?.call_outcomes.find((o) => o.id === id) ?? null;
    next = {
      ...next,
      call_outcome_id: id,
      outcome: cat?.short_name ?? null,
      outcome_label: cat?.description ?? null,
    };
  }
  if ('booked_at' in patch) {
    next = { ...next, booked_at: patch.booked_at ?? null };
  }
  if ('scheduled_at' in patch) {
    // Optimistic: store the bare date; server may return the full ISO
    // timestamp, which the next refetch will reconcile.
    next = { ...next, scheduled_at: patch.scheduled_at ?? null };
  }
  if ('link_sent' in patch) {
    next = { ...next, link_sent: patch.link_sent ?? null };
  }
  return next;
}